// Panoul mare de grafic: antet + bara de metrici + grafic + unelte de desen.
// Refolosit de «Список наблюдения», «Радар объёмов», «Терминал плотностей» și de modalul de grafic.

import { el, clear, bus, tfLabel } from '../core/util.js';
import { iconMoneda, iconBursa } from './coin-icon.js';
import { icon } from './icons.js';
import { settings, market } from '../core/state.js';
import { Chart } from '../chart/chart.js';
import { DrawingLayer } from '../chart/drawings.js';
import { DrawingToolbar } from '../chart/toolbar.js';
import { TIMEFRAMES } from '../core/exchanges.js';
import { techBar, tfButtons, toast, modal, toggle, tigerBtn } from './components.js';
import { openIndicatorsModal } from './indicators-modal.js';
import { openShareMenu } from './share-menu.js';

/** Setările graficului: uneltele vizuale care nu sunt indicatori. */
function openChartSettingsModal(panel) {
  const rows = [
    ['sx_levels_hidden', 'Скрыть уровни поддержки/сопротивления',
      'Автоматические уровни по фракталам', true],
    ['sx_grid_hidden', 'Скрыть сетку', 'Убрать вертикальные и горизонтальные линии', true],
  ];
  const body = el('div', { class: 'chart-settings' }, rows.map(([key, title, sub, inverted]) => toggle(
    inverted ? !settings.get(key, false) : settings.get(key, false),
    (value) => {
      settings.set(key, inverted ? !value : value);
      panel.applyChartSettings();
    },
    { title, subtitle: sub },
  )));
  return modal({ title: 'Настройки графика', body, width: 460 });
}

export class ChartPanel {
  constructor(opts = {}) {
    this.exchange = opts.exchange || 'binance';
    this.market = opts.market || 'futures';
    this.symbol = opts.symbol || 'BTCUSDT';
    this.tf = opts.tf || settings.get('sx_modal_tf', '1m');
    this.showToolbar = opts.showToolbar !== false;
    this.onSymbolChange = opts.onSymbolChange || null;
    this.unsubs = [];
  }

  mount(host) {
    this.root = el('div', { class: 'chart-panel' });
    this.headEl = el('div', { class: 'cp-head' });
    this.techEl = el('div', { class: 'cp-tech' });
    this.bodyEl = el('div', { class: 'cp-body' });
    this.root.append(this.headEl, this.techEl, this.bodyEl);
    host.append(this.root);

    this.#renderHead();

    this.chart = new Chart(this.bodyEl, {
      exchange: this.exchange,
      market: this.market,
      symbol: this.symbol,
      tf: this.tf,
      indicators: settings.get('sx_indicators', { volume: true }),
      limit: 1000,
      showLevels: !settings.get('sx_levels_hidden', false),
      onBar: () => this.#renderTech(),
    });

    this.chart.load().then(() => this.#renderTech()).catch((err) => {
      this.bodyEl.append(el('div', { class: 'card-error', text: 'Не удалось загрузить свечи' }));
      console.warn('[chart-panel]', err);
    });

    if (this.showToolbar) {
      this.drawings = new DrawingLayer(this.chart, this.chart.mainEl);
      this.toolbar = new DrawingToolbar(this.drawings, this.root);
      this.drawings.onAlertToggle = (drawing) => this.#toggleLineAlert(drawing);
      // Linia cu alertă a fost mutată: alerta veche se scoate, una nouă se pune la noul preț.
      this.drawings.onAlertMoved = async (drawing) => {
        await this.#toggleLineAlert(drawing);
        if (!drawing.alertId) await this.#toggleLineAlert(drawing);
      };
      // Ștergerea unei linii cu alertă trebuie să ia și alerta de pe server.
      this.drawings.onAlertRemoved = async (drawing) => {
        await fetch(`/api/alerts/${drawing.alertId}/delete`, { method: 'POST' }).catch(() => {});
        bus.emit('alerts:changed');
      };
    }

    this.unsubs.push(bus.on('market:aggregates', () => this.#renderTech()));
    this.unsubs.push(bus.on('settings:sx_indicators', (value) => this.chart.setIndicators(value)));
    this.unsubs.push(bus.on('settings:sx_techdata_compact', () => this.#renderTech()));
    return this;
  }

  // ------------------------------------------------------------------ antet

  #renderHead() {
    clear(this.headEl);
    const bookmarked = new Set(settings.get('sx_bookmarks', []) || []).has(this.symbol);

    this.headEl.append(
      el('div', {
        class: 'cp-symbol', title: 'Скопировать тикер',
        onclick: () => {
          navigator.clipboard?.writeText(this.symbol);
          toast('Тикер скопирован в буфер обмена');
        },
      }, [
        iconMoneda(this.symbol, 22),
        el('span', { class: 'cp-symbol-text', text: this.symbol }),
        el('span', { class: 'cp-ex', title: `Биржа: ${this.exchange}` }, [iconBursa(this.exchange, 14)]),
        el('span', { class: 'cp-symbol-mkt', text: this.market === 'futures' ? '.F' : '.S' }),
      ]),

      tigerBtn(this.symbol, this.exchange, this.market),

      el('button', {
        class: `cp-btn${bookmarked ? ' on' : ''}`,
        dataset: { act: 'bookmark' },
        title: 'В закладки',
        onclick: (event) => {
          const marks = new Set(settings.get('sx_bookmarks', []) || []);
          const on = !marks.has(this.symbol);
          if (on) marks.add(this.symbol); else marks.delete(this.symbol);
          settings.set('sx_bookmarks', [...marks]);
          const button = event.currentTarget;
          button.classList.toggle('on', on);
          clear(button).append(icon(on ? 'bookmarkOn' : 'bookmark', 15));
        },
      }, [icon(bookmarked ? 'bookmarkOn' : 'bookmark', 15)]),

      el('button', {
        class: 'cp-btn', dataset: { act: 'shot' }, title: 'Скриншот графика в буфер обмена',
        onclick: () => this.screenshot(),
      }, [icon('camera', 15)]),

      el('button', {
        class: 'cp-btn', dataset: { act: 'alert' }, title: 'Поставить уровень-алерт по текущей цене',
        onclick: () => this.createAlert(),
      }, [icon('bell', 15)]),

      // Un singur buton pentru tot ce înseamnă „trimite mai departe": chat, idee,
      // link, captură. Erau patru butoane separate în bară și nu încăpeau.
      el('button', {
        class: 'cp-btn', dataset: { act: 'share' }, title: 'Поделиться: в чат, в идею, ссылкой',
        onclick: (e) => openShareMenu(this, e.currentTarget),
      }, [icon('share', 15)]),

      el('button', {
        class: 'cp-btn cp-btn-wide', dataset: { act: 'indicators' }, title: 'Индикаторы',
        onclick: () => openIndicatorsModal(),
      }, [icon('fa-wave-square', 14), el('span', { text: 'Индикаторы' })]),

      el('button', {
        class: 'cp-btn', dataset: { act: 'settings' }, title: 'Настройки графика',
        onclick: () => openChartSettingsModal(this),
      }, [icon('gear', 15)]),

      el('div', { class: 'cp-head-spacer' }),

      this.tfGroup = tfButtons(this.tf, (tf) => this.setTimeframe(tf)),

      el('button', {
        class: 'cp-btn', dataset: { act: 'customtf' }, title: 'Свой таймфрейм',
        onclick: () => this.#askCustomTimeframe(),
      }, [icon('ellipsis', 14)]),

      el('div', { class: 'cp-head-sep' }),

      el('button', {
        class: 'cp-btn', dataset: { act: 'end' }, title: 'В конец графика',
        onclick: () => this.chart.scrollToRealtime(),
      }, [icon('chevronRight', 15)]),

      el('button', {
        class: 'cp-btn', dataset: { act: 'full' }, title: 'На весь экран',
        onclick: () => this.toggleFullscreen(),
      }, [icon('fullscreen', 14)]),
    );
  }

  /** Timeframe-uri în afara celor șase standard (ScalpX: butonul „…"). */
  #askCustomTimeframe() {
    const extra = ['3m', '30m', '2h', '6h', '12h', '1w'];
    const body = el('div', { class: 'tf-custom' }, extra.map((tf) => el('button', {
      class: `btn${tf === this.tf ? ' primary' : ''}`,
      text: tfLabel(tf),
      onclick: () => { this.setTimeframe(tf); close(); },
    })));
    const { close } = modal({ title: 'Свой таймфрейм', body, width: 360 });
  }

  async shareLink() {
    const url = `${location.origin}/screener/coins/?symbol=${this.symbol}&tf=${this.tf}`;
    try {
      await navigator.clipboard.writeText(url);
      toast('Ссылка скопирована', 'success');
    } catch {
      toast('Браузер не разрешил доступ к буферу обмена', 'error');
    }
  }

  toggleFullscreen() {
    if (document.fullscreenElement) { document.exitFullscreen(); return; }
    this.root.requestFullscreen?.()
      .then(() => setTimeout(() => this.chart?.resize(), 120))
      .catch(() => toast('Браузер не разрешил полноэкранный режим', 'error'));
  }

  #renderTech() {
    const row = market.get(this.symbol);
    clear(this.techEl);
    this.techEl.append(techBar(row, this.chart?.tech()));
  }

  // ---------------------------------------------------------------- comenzi

  async setSymbol(symbol) {
    if (symbol === this.symbol) return;
    this.symbol = symbol;
    settings.set('sx_coins_symbol', symbol);
    this.#renderHead();
    await this.chart.setSymbol(symbol, { exchange: this.exchange, market: this.market });
    this.drawings?.load();
    this.#renderTech();
    this.onSymbolChange?.(symbol);
  }

  async setTimeframe(tf) {
    this.tf = tf;
    settings.set('sx_modal_tf', tf);
    // Timeframe-ul poate veni și din meniul „…" sau dintr-un link, nu doar din butoane —
    // grupul trebuie să reflecte starea reală, altfel rămâne bifat cel vechi.
    this.tfGroup?.querySelectorAll('.tf-btn').forEach((b) => {
      b.classList.toggle('active', b.dataset.tf === tf);
    });
    await this.chart.setTimeframe(tf);
    this.#renderTech();
    this.drawings?.render();
  }

  /** Reaplică opțiunile vizuale după modalul de setări. */
  applyChartSettings() {
    this.chart.setLevelsVisible(!settings.get('sx_levels_hidden', false));
    this.chart.setGridVisible(!settings.get('sx_grid_hidden', false));
  }

  async setExchange(exchange, marketKind) {
    this.exchange = exchange;
    if (marketKind) this.market = marketKind;
    this.#renderHead();
    await this.chart.setSymbol(this.symbol, { exchange, market: this.market });
    this.drawings?.load();
  }

  /**
   * Captura graficului — cu desenele de pe el.
   *
   * Merge in doua locuri deodata: in clipboardul sistemului, pentru cine lucreaza pe
   * calculator, si in buzunarul aplicatiei, de unde se poate atasa la o publicatie sau
   * la un mesaj fara sa depinda de ce permite browserul.
   */
  async screenshot() {
    const { faCaptura, pastreaza, inClipboard } = await import('./captura.js');
    const url = faCaptura(this.chart, this.drawings);
    if (!url) { toast('Не удалось снять график', 'error'); return; }

    pastreaza(url, this.symbol);
    const inBuffer = await inClipboard(url);
    toast(inBuffer
      ? 'Снимок в буфере обмена и готов к публикации'
      : 'Снимок готов — прикрепите его к публикации или сообщению', 'success');
  }

  /**
   * Dublu-clic pe o linie orizontală: armează sau dezarmează alerta la acel preț.
   * Alerta trăiește pe server, deci se declanșează și cu tabul închis.
   */
  async #toggleLineAlert(drawing) {
    if (drawing.alertId) {
      await fetch(`/api/alerts/${drawing.alertId}/delete`, { method: 'POST' });
      delete drawing.alertId;
      this.drawings.save();
      this.drawings.render();
      toast('Уровень снят');
      bus.emit('alerts:changed');
      return;
    }
    const [a, b] = drawing.points;
    const sloped = b && b.price !== a.price && b.time !== a.time;
    // Pentru o linie înclinată trimitem ambele capete: serverul extinde dreapta
    // până în momentul verificării, deci alerta urmează linia.
    const anchor = sloped ? [[a.time, a.price], [b.time, b.price]] : null;
    const price = sloped
      ? a.price + ((b.price - a.price) / (b.time - a.time)) * (Date.now() / 1000 - a.time)
      : a.price;

    try {
      const resp = await fetch('/api/alerts/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: this.symbol, price,
          exchange: this.exchange, market: this.market, direction: 'cross',
          color: drawing.color, anchor,
        }),
      });
      if (!resp.ok) throw new Error(resp.statusText);
      const data = await resp.json();
      drawing.alertId = data.alert.id;
      this.drawings.save();
      this.drawings.render();
      toast(sloped ? 'Наклонный уровень армирован' : `Уровень ${price.toFixed(4)} армирован`, 'success');
      bus.emit('alerts:changed');
    } catch (err) {
      toast('Не удалось поставить уровень', 'error');
      console.warn('[alert]', err);
    }
  }

  /** Alertă la prețul curent — verificată pe server. */
  async createAlert() {
    const row = market.get(this.symbol);
    if (!row) return;
    try {
      const resp = await fetch('/api/alerts/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: this.symbol, price: row.price,
          exchange: this.exchange, market: this.market, direction: 'cross',
        }),
      });
      if (!resp.ok) throw new Error(resp.statusText);
      toast(`Уровень ${row.price} поставлен`, 'success');
      bus.emit('alerts:changed');
    } catch (err) {
      toast('Не удалось поставить уровень', 'error');
      console.warn('[alert]', err);
    }
  }

  destroy() {
    this.unsubs.forEach((fn) => fn());
    this.toolbar?.destroy();
    this.drawings?.destroy();
    this.chart?.destroy();
    this.root?.remove();
  }
}
